import { getPictureUrl } from 'core/api/pictures';
import Breadcrumbs from 'core/components/common/Breadcrumbs';
import { useLinks } from 'core/components/linksContext';
import { useSettings } from 'core/components/settingsContext';
import { button, column, container, ieFix, reverseLink, row, title } from 'core/style';
import injectSheet, { createStyles } from 'core/style/injectSheet';
import { ITheme, WithStyles } from 'core/style/interfaces';
import { formatToLocalDayMonth, formatToLocalTime, isMobile } from 'core/utils';
import { getLastSeminarDateWithDuration, getLastWebinarDateWithDuration } from 'core/utils/product-forms/productForms';
import React from 'react';
import FeedbackButton from 'site/components/feedback-product/FeedbackButton';
import ProductFormsIcons from 'site/components/product-form/ProductFormsIcons';
import { GetPrivateProductDetailDataQuery, InviteLink, ProductFormPublicTypeEnum } from 'site/graphql';

type IPrivateProductDetailHeaderProps = {
    productId: number;
    product: GetPrivateProductDetailDataQuery['purchasedProduct'];
} & WithStyles<typeof styles>;

const PrivateProductDetailHeader: React.FC<IPrivateProductDetailHeaderProps> = ({ classes, product, productId }) => {
    const { productForms = [] } = product;
    const links = useLinks();
    const settings = useSettings();

    const someCourseEnded = productForms.some(pf => {
        switch (pf.type) {
            case ProductFormPublicTypeEnum.Webinar:
                return getLastWebinarDateWithDuration(pf) < new Date();
            case ProductFormPublicTypeEnum.Seminar:
                return getLastSeminarDateWithDuration(pf) < new Date();
            default:
                return false;
        }
    });
    const inviteLink: InviteLink = product.inviteLink;

    return (
        <div className={classes.detail__top}>
            <div className={classes.detail__container}>
                <div className={classes.top}>
                    <div className={classes.top__main}>
                        <Breadcrumbs
                            contrast
                            items={[
                                { href: links.account.url(), title: 'Личный кабинет' },
                                { href: links.accountCourses.url(), title: 'Мои курсы' },
                                { title: product.name },
                            ]}
                        />
                        <h1 className={classes.top__title}>{product.name}</h1>
                        <p className={classes.top__shortDesc}>{product.shortDescription}</p>
                        <links.productDetail.Link
                            className={classes.top__button}
                            to={links.productDetail.url(product.id)}
                        >
                            Перейти на описание курса
                        </links.productDetail.Link>
                        {isMobile() && (
                            <FeedbackButton
                                productId={productId}
                                accentButton
                                openModalIfFeedbackNotExist={someCourseEnded}
                            />
                        )}
                        <ProductFormsIcons
                            productForms={productForms}
                            classes={{
                                wrapper: classes.formsFeatures,
                                item: classes.formsFeatures__item,
                                text: classes.formsFeatures__text,
                            }}
                        />
                        {inviteLink && (
                            <>
                                <a href={inviteLink.link} className={classes.telegram}>
                                    Перейти в telegram чат
                                </a>
                                Ссылка действительна до{' '}
                                {`${formatToLocalDayMonth(inviteLink?.dateExpire)}, ${formatToLocalTime(
                                    inviteLink?.dateExpire,
                                )}`}
                            </>
                        )}
                    </div>
                    <div className={classes.top__sidebar}>
                        <div className={classes.sidebar__preview}>
                            <div className={classes.preview}>
                                <div
                                    className={classes.preview__image}
                                    style={{
                                        background: `url("${getPictureUrl(
                                            product.pictureId,
                                            '370x180',
                                            settings,
                                        )}") center center / cover no-repeat`,
                                    }}
                                />
                                <div className={classes.preview__content}>
                                    <links.productDetail.Link
                                        className={classes.preview__button}
                                        to={links.productDetail.url(product.id)}
                                    >
                                        Перейти на описание курса
                                    </links.productDetail.Link>
                                    {!isMobile() && (
                                        <FeedbackButton
                                            productId={productId}
                                            accentButton
                                            openModalIfFeedbackNotExist={someCourseEnded}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const styles = (theme: ITheme) =>
    createStyles({
        detail__top: {
            backgroundColor: theme.colors.contrastBg,
            color: theme.colors.contrastBgText,
        },
        detail__container: {
            ...container(theme, { gutter: 30 }),
        },
        top: {
            ...row(),
        },
        top__main: {
            ...column(),

            [theme.breakpoints.up('sm')]: {
                ...column({ cols: 8 }),
                minHeight: '340px',
            },
        },
        top__title: {
            ...title({ size: 'h1', color: 'white' }),
            marginBottom: '1.5rem',
            marginRight: '3rem',
        },
        top__shortDesc: {
            color: '#CCCCCC',
        },
        top__button: {
            ...button(theme),

            backgroundColor: 'white',
            color: '#999999',
            fontWeight: 'bold',
            fontSize: 0.9 + 'rem',

            transition: 'background-color 0.25s ease-out, color 0.25s ease-out',
            '&:hover': {
                borderColor: '#CCCCCC',
                color: '#CCCCCC',
            },

            [theme.breakpoints.up('xs')]: {
                display: 'none',
            },
            [theme.breakpoints.down('xs')]: {
                display: 'block',
            },
        },
        top__sidebar: {
            ...column(),
            margin: '1rem 0',

            [theme.breakpoints.up('sm')]: {
                margin: '2.3rem 0',
                ...column({ cols: 4 }),
            },
        },
        formsFeatures: {
            flexDirection: 'row',
            flexWrap: 'wrap',
            maxWidth: '500px',
            padding: '10px 0',
            display: 'none',

            [theme.breakpoints.up('sm')]: {
                display: 'flex',
            },
        },
        formsFeatures__item: {
            flex: '1 0 0',
            display: 'flex',
            flexWrap: 'nowrap',
            alignItems: 'center',
            marginBottom: '1rem',
            paddingRight: '5px',
            maxWidth: 160,
            flexBasis: '25%',

            ...ieFix({ flex: '1 0 auto' }),
        },
        formsFeatures__text: {
            color: '#999999',
            margin: 0,
        },
        sidebar__preview: {},
        preview: {
            [theme.breakpoints.down('xs')]: {
                display: 'none',
            },
        },
        preview__image: {
            height: '180px',

            [theme.breakpoints.down('xs')]: {
                marginLeft: 'calc((-100vw + 100%)/2)',
                marginRight: 'calc((-100vw + 100%)/2)',
            },
        },
        preview__content: {
            backgroundColor: 'white',
            padding: '1.5rem',

            [theme.breakpoints.up('sm')]: {
                padding: '1rem',
            },

            [theme.breakpoints.up('lg')]: {
                padding: '1.5rem',
            },
        },
        preview__button: {
            ...button(theme),
            backgroundColor: 'white',
            border: `1px solid ${theme.colors.contrastBgMedium}`,
            color: '#999999',
            fontWeight: 'bold',
            display: 'none',
            fontSize: 0.9 + 'rem',
            transition: 'background-color 0.25s ease-out, color 0.25s ease-out',

            '&:hover': {
                borderColor: '#CCCCCC',
                color: '#808080',
            },

            [theme.breakpoints.up('sm')]: {
                display: 'block',
            },
        },
        telegram: {
            ...reverseLink(theme.colors.accent),
            display: 'block',
            margin: 10,
            textDecoration: 'none',
            '&:hover': {
                color: theme.colors.accentLight,
            },
        },
    });

export default injectSheet(styles, 'PrivateProductDetailHeader')(PrivateProductDetailHeader);
